import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { mockData } from '../data/mockData';
import { makePhoneCall, sendEmail, openGoogleMaps, contactWhatsApp } from '../utils/buttonActions';
import { submitNetlifyForm } from '../utils/netlifyForms';
import SectionHeader from './SectionHeader';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', service: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await submitNetlifyForm('contact', formData);

      setIsSubmitted(true);
      const msg = `Aim Flex Contact Request
Name: ${formData.name}
Phone: ${formData.phone}
Email: ${formData.email}
Service: ${formData.service || 'Not specified'}
Message: ${formData.message}`;
      setTimeout(() => contactWhatsApp(msg), 1000);
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('Unable to send your message right now. Please call or WhatsApp us.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: <Phone className="h-5 w-5" strokeWidth={2} />, title: 'PHONE', detail: mockData.contact.phone, sub: 'Mon-Sat 9AM-7PM', action: makePhoneCall },
    { icon: <Mail className="h-5 w-5" strokeWidth={2} />, title: 'EMAIL', detail: mockData.contact.email, sub: 'Reply within 24hrs', action: sendEmail },
    { icon: <MapPin className="h-5 w-5" strokeWidth={2} />, title: 'LOCATION', detail: '34, EMS Nagar, Ponmeni', sub: 'Madurai, TN 625016', action: openGoogleMaps },
    { icon: <Clock className="h-5 w-5" strokeWidth={2} />, title: 'HOURS', detail: 'Mon-Sat: 9AM-7PM', sub: 'Sunday: Closed', action: null },
  ];

  const services = ['Flex Printing', 'Vinyl Printing', 'Frame Welding', 'Signage Installation', 'Custom Design', 'Other'];

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white border-b-2 border-[#0A0A0A]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeader
          eyebrow="Dispatch Desk"
          title="Send The"
          accent="Job Brief"
          copy="Share a rough size, material idea, or location. We will help convert it into a production-ready quote."
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-reveal>
          {/* Contact Cards */}
          <div className="space-y-4">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                onClick={info.action ? () => info.action() : undefined}
                className={`border-2 border-[#0A0A0A] p-5 flex items-start space-x-4 transition-all duration-200 ${
                  info.action ? 'cursor-pointer hover:bg-[#0A0A0A] hover:text-white group' : ''
                }`}
                data-testid={`contact-info-${index}`}
              >
                <div className="text-[#FF2A00] group-hover:text-[#FF2A00]">{info.icon}</div>
                <div>
                  <h4 className="text-xs font-bold tracking-widest mb-1">{info.title}</h4>
                  <p className="text-sm font-medium">{info.detail}</p>
                  <p className="text-xs text-[#999] group-hover:text-[#666] mt-1">{info.sub}</p>
                </div>
              </div>
            ))}

            {/* Quick Contact */}
            <div className="border-2 border-[#FF2A00] bg-[#FFF5F3] p-5">
              <h4 className="text-xs font-bold tracking-widest uppercase mb-3">IMMEDIATE ASSISTANCE</h4>
              <div className="flex gap-3">
                <button
                  onClick={() => makePhoneCall()}
                  className="flex-1 bg-[#0A0A0A] text-white py-3 text-xs font-bold tracking-widest uppercase border-2 border-[#0A0A0A] hover:bg-[#FF2A00] transition-colors duration-200"
                  data-testid="contact-call-btn"
                >
                  CALL
                </button>
                <button
                  onClick={() => contactWhatsApp('Hi! I need immediate assistance.')}
                  className="flex-1 bg-[#FF2A00] text-white py-3 text-xs font-bold tracking-widest uppercase border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] transition-colors duration-200"
                  data-testid="contact-whatsapp-btn"
                >
                  WHATSAPP
                </button>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="border-2 border-[#0A0A0A] shadow-brutal bg-white" data-testid="contact-form-card">
              <div className="bg-[#0A0A0A] text-white p-6">
                <span className="text-sm font-bold tracking-widest uppercase">SEND US A MESSAGE</span>
              </div>

              <div className="p-6 lg:p-8">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <div className="w-16 h-16 bg-[#0A0A0A] flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="h-8 w-8 text-[#FF2A00]" strokeWidth={2} />
                    </div>
                    <h3 className="text-xl font-black tracking-tighter uppercase mb-2">MESSAGE SENT</h3>
                    <p className="text-sm text-[#666]">We'll get back to you within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold tracking-widest text-[#0A0A0A] mb-2">FULL NAME *</label>
                        <input
                          name="name" value={formData.name} onChange={handleInputChange}
                          placeholder="Your name" required
                          className="w-full p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold tracking-widest text-[#0A0A0A] mb-2">EMAIL *</label>
                        <input
                          type="email" name="email" value={formData.email} onChange={handleInputChange}
                          placeholder="your@email.com" required
                          className="w-full p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold tracking-widest text-[#0A0A0A] mb-2">PHONE *</label>
                        <input
                          type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                          placeholder="+91 9876543210" required
                          className="w-full p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold tracking-widest text-[#0A0A0A] mb-2">SERVICE</label>
                        <select
                          name="service" value={formData.service} onChange={handleInputChange}
                          className="w-full p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm"
                          data-testid="contact-input-service"
                        >
                          <option value="">Select a service</option>
                          {services.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold tracking-widest text-[#0A0A0A] mb-2">PROJECT DETAILS *</label>
                      <textarea
                        name="message" value={formData.message} onChange={handleInputChange}
                        placeholder="Tell us about your project..." rows={5} required
                        className="w-full p-3 border-2 border-[#0A0A0A] bg-white focus:border-[#FF2A00] focus:outline-none transition-colors text-sm resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>

                    <button
                      type="submit" disabled={isSubmitting}
                      className="w-full bg-[#FF2A00] text-white py-4 text-sm font-bold tracking-widest uppercase border-2 border-[#0A0A0A] shadow-brutal hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-50 flex items-center justify-center"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent mr-2"></div>
                          SENDING...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" strokeWidth={2} />
                          SEND MESSAGE
                        </>
                      )}
                    </button>
                    {submitError && (
                      <p className="text-sm font-medium text-[#FF2A00] text-center" role="alert">
                        {submitError}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-12 border-2 border-[#0A0A0A] cursor-pointer hover:shadow-brutal transition-all duration-200" onClick={openGoogleMaps} data-testid="contact-map" data-reveal>
          <div className="bg-[#F9F9F6] h-48 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="h-8 w-8 mx-auto mb-3 text-[#FF2A00]" strokeWidth={2} />
              <p className="text-xs font-bold tracking-widest uppercase text-[#0A0A0A]">CLICK TO OPEN GOOGLE MAPS</p>
              <p className="text-xs text-[#666] mt-1">{mockData.contact.fullAddress}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
